import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useState,
	useMemo,
	useRef,
} from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import SlideshowModal from './SlideshowModal';
import {
	dlViewSlideshowSettings,
} from 'Vulture/DataLayer';

import {
	getFDValue,
} from 'Vulture/Helpers';

// Import styles
import style from './SlideshowTable.module.css';
import VultureTable from './VultureTable';

// Helper function to check if a slide should be disabled based on scheduling
// const isSlideDisabledByScheduling = (slide) => {
// 	// If scheduling is not enabled, slide is always active
// 	if (!slide.scheduling) {
// 		return false;
// 	}

// 	const now = new Date();

// 	// Check date range if use_dates is enabled
// 	if (slide.use_dates) {
// 		const startDate = slide.start_date ? new Date(slide.start_date) : null;
// 		const endDate = slide.end_date ? new Date(slide.end_date) : null;

// 		if (startDate && now < startDate) {
// 			return true; // Before start date
// 		}
// 		if (endDate && now > endDate) {
// 			return true; // After end date
// 		}
// 	}

// 	// Check time range if use_times is enabled
// 	if (slide.use_times) {
// 		const currentTime = now.toTimeString().slice(0, 8); // HH:MM:SS format
// 		const startTime = slide.start_time;
// 		const endTime = slide.end_time;

// 		if (startTime && currentTime < startTime) {
// 			return true; // Before start time
// 		}
// 		if (endTime && currentTime > endTime) {
// 			return true; // After end time
// 		}
// 	}

// 	// Check days if use_days is enabled
// 	if (slide.use_days && slide.days && slide.days.length > 0) {
// 		const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
// 		if (!slide.days.includes(currentDay)) {
// 			return true; // Current day not in allowed days
// 		}
// 	}

// 	return false; // Slide is active
// };

const daysFilter = (rows, id, filterValue) => {
	return rows.filter((row) => {
		let isInAllSearch = false;
		if (row.original.daysForSearch) {
			let isInSearch = true;
			const rowValue = row.original.daysForSearch;
			const filterValues = filterValue.split(' ');
			filterValues.forEach((val) => {
				let inSearch = false;
				if (val === '' || !isInSearch) {
					return;
				}
				rowValue.forEach((rowVal) => {
					if (rowVal.includes(val)) {
						inSearch = true;
					}
				});
				isInSearch = inSearch;
			});
			isInAllSearch = isInSearch;
		}
		return isInAllSearch;
	});
};

const DEFAULT_DATA_COL_MIN_WIDTH = 80;
const ROW_SELECT_COLUMN_WIDTH_PX = 52;
const DEFAULT_DATA_COL_WIDTHS = {
	imgElm: 180,
	description: 180,
	resolution: 102,
	order:      100,
	duration:   100,
	playingOn:  220,
};

const computeDefaultDataColumnWidths = (visibleColumnIds, containerWidth) => {
	const hasDescription = visibleColumnIds.includes('description');
	const hasPlayingOn = visibleColumnIds.includes('playingOn');

	const fixed = visibleColumnIds.reduce((sum, id) => {
		if (id === 'rowSelect') return sum + ROW_SELECT_COLUMN_WIDTH_PX;
		if (id === 'description') return sum;
		if (id === 'playingOn' && hasDescription) return sum;
		return sum + (DEFAULT_DATA_COL_WIDTHS[id] || DATA_COLUMN_MIN_WIDTHS[id] || DEFAULT_DATA_COL_MIN_WIDTH);
	}, 0);

	const flexibleArea = Math.max(0, containerWidth - fixed);
	const descMin = DATA_COLUMN_MIN_WIDTHS['description'] || DEFAULT_DATA_COL_MIN_WIDTH;
	const playOnMin = DATA_COLUMN_MIN_WIDTHS['playingOn'] || DEFAULT_DATA_COL_MIN_WIDTH;

	const result = {};
	visibleColumnIds.forEach((id) => {
		if (id === 'rowSelect') return;
		if (id === 'description') {
			if (hasPlayingOn) {
				const playOnWidth = Math.max(playOnMin, Math.round(containerWidth * 0.30));
				result[id] = Math.max(descMin, flexibleArea - playOnWidth);
			} else {
				result[id] = Math.max(descMin, flexibleArea);
			}
		} else if (id === 'playingOn') {
			if (hasDescription) {
				result[id] = Math.max(playOnMin, Math.round(containerWidth * 0.30));
			} else {
				result[id] = Math.max(playOnMin, flexibleArea);
			}
		} else {
			result[id] = DEFAULT_DATA_COL_WIDTHS[id] || DATA_COLUMN_MIN_WIDTHS[id] || DEFAULT_DATA_COL_MIN_WIDTH;
		}
	});
	return result;
};
const DATA_COLUMN_MIN_WIDTHS = {
	imgElm:      90,
	description: 160,
	resolution:  90,
	order:       80,
	duration:    90,
	startDate:   110,
	endDate:     110,
	startTime:   100,
	endTime:     100,
	daysCol:     110,
	playingOn:   150,
};

const getSlideshowViewCacheKey = (accountUUID) => {
	return `slideshow_table_view_${accountUUID || 'global'}`;
};

const defaultSlideshowViewPrefs = {
	hiddenDataColumnIds: ['order', 'duration', 'resolution', 'startDate', 'endDate', 'startTime', 'endTime', 'daysCol'],
	dataColumnWidthsPx:  {},
};



const PlayingOnChips = ({ slide, systemToSystemGroupObjects, checkGroupForComponentRelation, fd, editObject, onOpenModal, isExpanded, onToggleExpand, onPropertyExpandChange }) => {
	const [ expandedPropertyUUIDs, setExpandedPropertyUUIDs, ] = useState(new Set());

	const [ computedVisibleCount, setComputedVisibleCount, ] = useState(null);
	const containerRef = useRef(null);
	const chipElemsRef = useRef([]);
	const addBtnRef = useRef(null);

	const assignedObjects = useMemo(() => {
		const seen = new Set();
		return (systemToSystemGroupObjects || []).filter((obj) => {
			const key = obj.isBundled ? obj.bundleID : obj.systemID;
			if (seen.has(key)) return false;
			seen.add(key);
			return checkGroupForComponentRelation(obj, slide);
		});
	}, [ systemToSystemGroupObjects, checkGroupForComponentRelation, slide, ]);

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const groups = [];
		Object.entries(fd).forEach(([ key, val, ]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const systemIDsInProperty = new Set();
			Object.keys(val).forEach((subKey) => {
				if (subKey.startsWith('system.')) systemIDsInProperty.add(subKey.split('.')[1]);
			});
			const assignedInProperty = assignedObjects.filter((obj) => {
				if (obj.isBundled) return (obj.systems || []).some((sysKey) => {
					const sysUUID = sysKey.startsWith('system.') ? sysKey.split('.')[1] : sysKey;
					return systemIDsInProperty.has(sysUUID);
				});
				return systemIDsInProperty.has(obj.systemID);
			});
			if (assignedInProperty.length > 0) {
				groups.push({ sgUUID, sgName, assignedSystems: assignedInProperty, });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [ fd, assignedObjects, ]);

	const groupedObjectKeys = useMemo(() => {
		const keys = new Set();
		propertyGroups.forEach((pg) => {
			pg.assignedSystems.forEach((obj) => keys.add(obj.isBundled ? obj.bundleID : obj.systemID));
		});
		return keys;
	}, [ propertyGroups, ]);

	const ungroupedAssigned = useMemo(() => {
		return assignedObjects.filter((obj) => !groupedObjectKeys.has(obj.isBundled ? obj.bundleID : obj.systemID));
	}, [ assignedObjects, groupedObjectKeys, ]);

	const allTopLevelItems = useMemo(() => {
		return [
			...propertyGroups.map((pg) => ({ type: 'group', pg, })),
			...ungroupedAssigned.map((obj) => ({ type: 'ungrouped', obj, })),
		];
	}, [ propertyGroups, ungroupedAssigned, ]);

	const totalItems = allTopLevelItems.length;

	useLayoutEffect(() => {
		setComputedVisibleCount(null);
	}, [ totalItems, ]);

	// Trim chips until the trailing Assign button (the last element) fits within the
	// collapsed height; once it fits, the chips + "+N more" before it fit too.
	useLayoutEffect(() => {
		if (isExpanded || expandedPropertyUUIDs.size > 0) return;
		const container = containerRef.current;
		const addBtn = addBtnRef.current;
		if (!container || !addBtn) return;
		const containerBottom = container.getBoundingClientRect().bottom;
		const addBtnClipped = addBtn.getBoundingClientRect().bottom > containerBottom + 1;
		if (!addBtnClipped) {
			if (computedVisibleCount === null) {
				setComputedVisibleCount(chipElemsRef.current.filter(Boolean).length);
			}
			return;
		}
		setComputedVisibleCount((prev) => {
			const current = prev === null ? chipElemsRef.current.filter(Boolean).length : prev;
			return Math.max(0, current - 1);
		});
	}, [ computedVisibleCount, isExpanded, expandedPropertyUUIDs.size, ]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || typeof ResizeObserver === 'undefined') return;
		const ro = new ResizeObserver(() => {
			if (!isExpandedRef.current && expandedPropertyUUIDsRef.current.size === 0) setComputedVisibleCount(null);
		});
		ro.observe(container);
		return () => ro.disconnect();
	}, []);

	const isExpandedRef = useRef(isExpanded);
	isExpandedRef.current = isExpanded;

	const onPropertyExpandChangeRef = useRef(onPropertyExpandChange);
	onPropertyExpandChangeRef.current = onPropertyExpandChange;

	const expandedPropertyUUIDsRef = useRef(expandedPropertyUUIDs);
	expandedPropertyUUIDsRef.current = expandedPropertyUUIDs;

	const slideKeyRef = useRef(slide && slide.key);
	useLayoutEffect(() => {
		const cb = onPropertyExpandChangeRef.current;
		if (typeof cb !== 'function' || !slideKeyRef.current) return;
		if (expandedPropertyUUIDs.size === 0) {
			cb(slideKeyRef.current, false);
			return;
		}
		cb(slideKeyRef.current, true);
	}, [ expandedPropertyUUIDs.size, ]);

	const removeAssignment = useCallback((e, obj) => {
		e.stopPropagation();
		const toRemove = new Set(Object.keys(obj.componentUUIDs));
		const updated = { ...slide, component_uuids: (slide.component_uuids || []).filter((u) => !toRemove.has(u)), };
		editObject(updated, slide, () => {});
	}, [ slide, editObject, ]);

	const toggleProperty = useCallback((e, sgUUID) => {
		e.stopPropagation();
		setExpandedPropertyUUIDs((prev) => {
			const next = new Set(prev);
			if (next.has(sgUUID)) { next.delete(sgUUID); } else { next.add(sgUUID); }
			return next;
		});
	}, []);

	const removePropertyAssignment = useCallback((e, systems) => {
		e.stopPropagation();
		const toRemove = new Set();
		systems.forEach((obj) => Object.keys(obj.componentUUIDs).forEach((u) => toRemove.add(u)));
		const updated = { ...slide, component_uuids: (slide.component_uuids || []).filter((u) => !toRemove.has(u)), };
		editObject(updated, slide, () => {});
	}, [ slide, editObject, ]);

	if (assignedObjects.length === 0) {
		return (
			<div ref={containerRef} className={style.playingOnCell} onClick={onOpenModal}>
				<div className={style.playingOnAddBtn} onClick={(e) => { e.stopPropagation(); onOpenModal(e); }}>
					<CustomIcon icon='plus' /> Assign to Systems
				</div>
			</div>
		);
	}

	const measuringAll = computedVisibleCount === null;
	const shownItems = (isExpanded || measuringAll) ? allTopLevelItems : allTopLevelItems.slice(0, computedVisibleCount);
	const hiddenCount = (isExpanded || measuringAll) ? 0 : Math.max(0, totalItems - computedVisibleCount);
	const showLess = isExpanded;

	chipElemsRef.current = [];

	return (
		<div ref={containerRef} className={style.playingOnCell} onClick={onOpenModal}>
			{shownItems.map((item, i) => {
				const chipRef = (el) => { chipElemsRef.current[i] = el; };
				if (item.type === 'group') {
					const { pg, } = item;
					const isPgExpanded = expandedPropertyUUIDs.has(pg.sgUUID);
					return (
						<div key={pg.sgUUID}>
							<div ref={chipRef} className={style.playingOnChip}>
								<span className={style.chipLabel} title={pg.sgName}>{pg.sgName}</span>
								<span className={style.chipCount} onClick={(e) => toggleProperty(e, pg.sgUUID)}>
									{pg.assignedSystems.length}&nbsp;{isPgExpanded ? '▲' : '▼'}
								</span>
								<span className={style.chipX} onClick={(e) => removePropertyAssignment(e, pg.assignedSystems)}>×</span>
							</div>
							{isPgExpanded && (
								<div className={style.playingOnExpanded}>
									{pg.assignedSystems.map((obj) => {
										const key = obj.isBundled ? obj.bundleID : obj.systemID;
										const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
										return (
											<div key={key} className={style.playingOnChipSub}>
												<span className={style.chipSubLabel} title={label}>{label}</span>
												<span className={style.chipX} onClick={(e) => removeAssignment(e, obj)}>×</span>
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				}
				const { obj, } = item;
				const key = obj.isBundled ? obj.bundleID : obj.systemID;
				const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
				return (
					<div key={key} ref={chipRef} className={style.playingOnChip}>
						<span className={style.chipLabel} title={label}>{label}</span>
						<span className={style.chipX} onClick={(e) => removeAssignment(e, obj)}>×</span>
					</div>
				);
			})}
			{hiddenCount > 0 && (
				<div
					className={style.playingOnMoreBtn}
					onClick={(e) => { e.stopPropagation(); onToggleExpand && onToggleExpand(); }}
				>
					+{hiddenCount} more
				</div>
			)}
			{showLess && (
				<div
					className={style.playingOnMoreBtn}
					onClick={(e) => { e.stopPropagation(); onToggleExpand && onToggleExpand(); }}
				>
					Show less
				</div>
			)}
			<div ref={addBtnRef} className={style.playingOnAddBtn} onClick={(e) => { e.stopPropagation(); onOpenModal(e); }}>
				<CustomIcon icon='plus' /> Assign to Systems
			</div>
		</div>
	);
};

const SystemAssignmentModal = ({ slide, systemToSystemGroupObjects, checkGroupForComponentRelation, fd, editObject, buildPelicanData, onClose }) => {
	const [ search, setSearch, ] = useState('');
	const [ pendingUUIDs, setPendingUUIDs, ] = useState(() => [ ...(slide.component_uuids || []), ]);

	const bundleUUIDs = useMemo(() => {
		const map = {};
		(systemToSystemGroupObjects || []).forEach((obj) => {
			if (!obj.isBundled) return;
			if (!map[obj.bundleID]) map[obj.bundleID] = [];
			Object.keys(obj.componentUUIDs).forEach((u) => {
				if (!map[obj.bundleID].includes(u)) map[obj.bundleID].push(u);
			});
		});
		return map;
	}, [ systemToSystemGroupObjects, ]);

	const getObjUUIDs = useCallback((obj) => {
		if (obj.isBundled) return bundleUUIDs[obj.bundleID] || Object.keys(obj.componentUUIDs);
		return Object.keys(obj.componentUUIDs);
	}, [ bundleUUIDs, ]);

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const systemUUIDsMap = {};
		(systemToSystemGroupObjects || []).forEach((obj) => {
			if (!obj.isBundled) {
				systemUUIDsMap[obj.systemID] = Object.keys(obj.componentUUIDs);
			}
		});
		const groups = [];
		Object.entries(fd).forEach(([ key, val, ]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const uuids = [];
			Object.keys(val).forEach((subKey) => {
				if (!subKey.startsWith('system.')) return;
				const sysUUID = subKey.split('.')[1];
				const sysUUIDList = systemUUIDsMap[sysUUID];
				if (sysUUIDList) {
					sysUUIDList.forEach((u) => { if (!uuids.includes(u)) uuids.push(u); });
				}
			});
			if (uuids.length > 0) {
				groups.push({ sgUUID, sgName, uuids, });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [ fd, systemToSystemGroupObjects, ]);

	const deduped = useMemo(() => {
		const seen = new Set();
		return (systemToSystemGroupObjects || []).filter((obj) => {
			const key = obj.isBundled ? obj.bundleID : obj.systemID;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [ systemToSystemGroupObjects, ]);

	const propertyGroupItems = useMemo(() => {
		if (!fd) return {};
		const result = {};
		propertyGroups.forEach((pg) => {
			const pgSystemIDs = new Set();
			Object.entries(fd).forEach(([ key, val, ]) => {
				if (!key.startsWith(`system_group.${pg.sgUUID}`)) return;
				Object.keys(val).forEach((subKey) => {
					if (subKey.startsWith('system.')) pgSystemIDs.add(subKey.split('.')[1]);
				});
			});
			result[pg.sgUUID] = deduped.filter((obj) => {
				if (obj.isBundled) return (obj.systems || []).some((sysKey) => {
					const sysUUID = sysKey.startsWith('system.') ? sysKey.split('.')[1] : sysKey;
					return pgSystemIDs.has(sysUUID);
				});
				return pgSystemIDs.has(obj.systemID);
			});
		});
		return result;
	}, [ fd, propertyGroups, deduped, ]);

	const filteredGrouped = useMemo(() => {
		if (!search.trim()) return null;
		const q = search.toLowerCase();
		const groups = [];
		const coveredKeys = new Set();
		propertyGroups.forEach((pg) => {
			const pgItems = propertyGroupItems[pg.sgUUID] || [];
			const pgNameMatches = pg.sgName.toLowerCase().includes(q);
			const matchingItems = pgNameMatches
				? pgItems
				: pgItems.filter((obj) => {
						const name = (obj.isBundled ? obj.bundleName : obj.systemDisplayName) || '';
						return name.toLowerCase().includes(q);
					});
			if (matchingItems.length > 0) {
				groups.push({ pg, items: matchingItems, });
				matchingItems.forEach((obj) => coveredKeys.add(obj.isBundled ? obj.bundleID : obj.systemID));
			}
		});
		const ungrouped = deduped.filter((obj) => {
			const key = obj.isBundled ? obj.bundleID : obj.systemID;
			if (coveredKeys.has(key)) return false;
			const name = (obj.isBundled ? obj.bundleName : obj.systemDisplayName) || '';
			return name.toLowerCase().includes(q);
		});
		return { groups, ungrouped, };
	}, [ search, propertyGroups, propertyGroupItems, deduped, ]);

	const filtered = useMemo(() => {
		if (!search.trim()) return deduped;
		if (filteredGrouped) {
			const all = [];
			filteredGrouped.groups.forEach(({ items, }) => items.forEach((obj) => all.push(obj)));
			filteredGrouped.ungrouped.forEach((obj) => all.push(obj));
			return all;
		}
		return deduped;
	}, [ deduped, search, filteredGrouped, ]);

	const isChecked = useCallback((obj) => {
		const uuids = new Set(pendingUUIDs);
		return getObjUUIDs(obj).some((u) => uuids.has(u));
	}, [ pendingUUIDs, getObjUUIDs, ]);

	const toggle = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			if (objUUIDs.some((u) => s.has(u))) { objUUIDs.forEach((u) => s.delete(u)); }
			else { objUUIDs.forEach((u) => s.add(u)); }
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const assignGroup = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			objUUIDs.forEach((u) => s.add(u));
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const clearGroup = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			objUUIDs.forEach((u) => s.delete(u));
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const assignProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			uuids.forEach((u) => s.add(u));
			return Array.from(s);
		});
	}, []);

	const clearProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			uuids.forEach((u) => s.delete(u));
			return Array.from(s);
		});
	}, []);

	const assignAll = useCallback(() => {
		setPendingUUIDs((prev) => {
			const all = new Set(prev);
			(systemToSystemGroupObjects || []).forEach((obj) => Object.keys(obj.componentUUIDs).forEach((u) => all.add(u)));
			return Array.from(all);
		});
	}, [ systemToSystemGroupObjects, ]);

	const clearAll = useCallback(() => setPendingUUIDs([]), []);

	const save = useCallback(() => {
		const updated = { ...slide, component_uuids: pendingUUIDs, };
		editObject(updated, slide, () => { buildPelicanData(); onClose(); }, null, true);
	}, [ slide, pendingUUIDs, editObject, buildPelicanData, onClose, ]);


	const totalSystemsCount = deduped.length;

	const selectedSystemsCount = useMemo(() => {
		return deduped.filter((obj) => isChecked(obj)).length;
	}, [ deduped, isChecked, ]);

	const isAllAssigned = useMemo(() => {
		return deduped.length > 0 && deduped.every((obj) => isChecked(obj));
	}, [ deduped, isChecked, ]);

	const propertyRefsMap = useRef({});
	const assignmentListRef = useRef(null);

	const isSearching = search.trim().length > 0;

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Systems</span>
						<span className={style.assignmentModalDescription}>{slide.description}</span>
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalSystemsCount} systems...`}
							autoFocus
						/>
						{selectedSystemsCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedSystemsCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalActions}>
					<label className={style.assignmentModalAssignAllLabel}>
						<input
							type="checkbox"
							checked={isAllAssigned}
							ref={(node) => { if (node) node.indeterminate = selectedSystemsCount > 0 && !isAllAssigned; }}
							onChange={(e) => { if (e.target.checked) { assignAll(); } else { clearAll(); } }}
							onClick={(e) => e.stopPropagation()}
						/>
						<span>Assign to all {totalSystemsCount} systems</span>
					</label>
					<button type="button" className="button" onClick={clearAll}>Clear All</button>
				</div>
				{!isSearching && propertyGroups.length > 0 && (
					<div className={style.assignmentModalQuickJump}>
						<span className={style.assignmentModalQuickJumpLabel}>Quick:</span>
						{propertyGroups.map((pg) => (
							<button
								type="button"
								key={pg.sgUUID}
								className={style.assignmentModalQuickJumpItem}
								onClick={() => {
									const el = propertyRefsMap.current[pg.sgUUID];
									const listEl = assignmentListRef.current;
									if (el && listEl) {
										listEl.scrollTo({ top: el.getBoundingClientRect().top - listEl.getBoundingClientRect().top + listEl.scrollTop, behavior: 'smooth' });
									}
								}}
							>
								{pg.sgName}
							</button>
						))}
					</div>
				)}
				<div id="assign-modal-list-slideshow" ref={assignmentListRef} className={style.assignmentModalList}>
					{isSearching ? (
						<>
							{filteredGrouped && filteredGrouped.groups.map(({ pg, items, }) => {
								const checkedCount = items.filter((obj) => isChecked(obj)).length;
								const isPropertyAllChecked = items.length > 0 && items.every((obj) => isChecked(obj));
								const isPropertySomeChecked = items.some((obj) => isChecked(obj));
								return (
									<div key={pg.sgUUID}>
										<div className={style.assignmentModalPropertyHeader}>
											<input
												type="checkbox"
												checked={isPropertyAllChecked}
												ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
												onChange={(e) => { if (e.target.checked) { assignProperty(pg.uuids); } else { clearProperty(pg.uuids); } }}
												onClick={(e) => e.stopPropagation()}
											/>
											<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
											<span className={style.assignmentModalPropertyCount}>{checkedCount}/{items.length}</span>
											<span
												className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
												onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
											>
												{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
											</span>
										</div>
										{items.map((obj) => {
											const key = obj.isBundled ? obj.bundleID : obj.systemID;
											const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
											const allChecked = isChecked(obj);
											return (
												<div
													key={key}
													className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
													onClick={() => toggle(obj)}
												>
													<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
													<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
													{obj.isBundled && (
														<span
															className={style.assignmentModalToggleLink}
															onClick={(e) => { e.stopPropagation(); allChecked ? clearGroup(obj) : assignGroup(obj); }}
														>
															{allChecked ? 'Deselect all' : 'Select all'}
														</span>
													)}
												</div>
											);
										})}
									</div>
								);
							})}
							{filteredGrouped && filteredGrouped.ungrouped.map((obj) => {
								const key = obj.isBundled ? obj.bundleID : obj.systemID;
								const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
								const allChecked = isChecked(obj);
								return (
									<div
										key={key}
										className={`${style.assignmentModalItem}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(obj)}
									>
										<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
										<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
										{obj.isBundled && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); allChecked ? clearGroup(obj) : assignGroup(obj); }}
											>
												{allChecked ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								);
							})}
							{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No systems found.</div>}
						</>
					) : (
						propertyGroups.length > 0 ? propertyGroups.map((pg) => {
							const pgItems = propertyGroupItems[pg.sgUUID] || [];
							if (pgItems.length === 0) return null;
							const checkedCount = pgItems.filter((obj) => isChecked(obj)).length;
							const isPropertyAllChecked = pgItems.length > 0 && pgItems.every((obj) => isChecked(obj));
							const isPropertySomeChecked = pgItems.some((obj) => isChecked(obj));
							return (
								<div key={pg.sgUUID}>
									<span ref={(el) => { propertyRefsMap.current[pg.sgUUID] = el; }} style={{ display: 'block', height: 0 }} />
									<div
										className={style.assignmentModalPropertyHeader}
									>
										<input
											type="checkbox"
											checked={isPropertyAllChecked}
											ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
											onChange={(e) => { if (e.target.checked) { assignProperty(pg.uuids); } else { clearProperty(pg.uuids); } }}
											onClick={(e) => e.stopPropagation()}
										/>
										<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
										<span className={style.assignmentModalPropertyCount}>{checkedCount}/{pgItems.length}</span>
										<span
											className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
											onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
										>
											{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
										</span>
									</div>
									{pgItems.map((obj) => {
										const key = obj.isBundled ? obj.bundleID : obj.systemID;
										const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
										return (
											<div
												key={key}
												className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
												onClick={() => toggle(obj)}
											>
												<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
												<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
												{obj.isBundled && (
													<span
														className={style.assignmentModalToggleLink}
														onClick={(e) => { e.stopPropagation(); isChecked(obj) ? clearGroup(obj) : assignGroup(obj); }}
													>
														{isChecked(obj) ? 'Deselect all' : 'Select all'}
													</span>
												)}
											</div>
										);
									})}
								</div>
							);
						}) : (
							deduped.map((obj) => {
								const key = obj.isBundled ? obj.bundleID : obj.systemID;
								const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
								const allChecked = isChecked(obj);
								return (
									<div
										key={key}
										className={`${style.assignmentModalItem}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(obj)}
									>
										<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
										<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
										{obj.isBundled && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); allChecked ? clearGroup(obj) : assignGroup(obj); }}
											>
												{allChecked ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								);
							})
						)
					)}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};


const BulkSlideAssignmentModal = ({ systemToSystemGroupObjects, fd, onSave, onClose }) => {
	const [ search, setSearch, ] = useState('');
	const [ pendingUUIDs, setPendingUUIDs, ] = useState([]);

	const bundleUUIDs = useMemo(() => {
		const map = {};
		(systemToSystemGroupObjects || []).forEach((obj) => {
			if (!obj.isBundled) return;
			if (!map[obj.bundleID]) map[obj.bundleID] = [];
			Object.keys(obj.componentUUIDs).forEach((u) => {
				if (!map[obj.bundleID].includes(u)) map[obj.bundleID].push(u);
			});
		});
		return map;
	}, [ systemToSystemGroupObjects, ]);

	const getObjUUIDs = useCallback((obj) => {
		if (obj.isBundled) return bundleUUIDs[obj.bundleID] || Object.keys(obj.componentUUIDs);
		return Object.keys(obj.componentUUIDs);
	}, [ bundleUUIDs, ]);

	const propertyGroups = useMemo(() => {
		if (!fd) return [];
		const systemUUIDsMap = {};
		(systemToSystemGroupObjects || []).forEach((obj) => {
			const uuids = obj.isBundled
				? (bundleUUIDs[obj.bundleID] || Object.keys(obj.componentUUIDs))
				: Object.keys(obj.componentUUIDs);
			if (obj.isBundled) {
				(obj.systems || []).forEach((sysKey) => {
					const sysUUID = sysKey.startsWith('system.') ? sysKey.split('.')[1] : sysKey;
					systemUUIDsMap[sysUUID] = uuids;
				});
				return;
			}
			systemUUIDsMap[obj.systemID] = uuids;
		});
		const groups = [];
		Object.entries(fd).forEach(([ key, val, ]) => {
			if (!key.startsWith('system_group.')) return;
			const sgUUID = key.split('.')[1];
			const sgName = (val && val.display_name) ? val.display_name : sgUUID;
			const uuids = [];
			Object.keys(val).forEach((subKey) => {
				if (!subKey.startsWith('system.')) return;
				const sysUUID = subKey.split('.')[1];
				const sysUUIDList = systemUUIDsMap[sysUUID];
				if (sysUUIDList) {
					sysUUIDList.forEach((u) => { if (!uuids.includes(u)) uuids.push(u); });
				}
			});
			if (uuids.length > 0) {
				groups.push({ sgUUID, sgName, uuids, });
			}
		});
		groups.sort((a, b) => a.sgName.localeCompare(b.sgName));
		return groups;
	}, [ fd, systemToSystemGroupObjects, ]);

	const deduped = useMemo(() => {
		const seen = new Set();
		return (systemToSystemGroupObjects || []).filter((obj) => {
			const key = obj.isBundled ? obj.bundleID : obj.systemID;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [ systemToSystemGroupObjects, ]);

	const filtered = useMemo(() => {
		if (!search.trim()) return deduped;
		const q = search.toLowerCase();
		return deduped.filter((obj) => {
			const name = (obj.isBundled ? obj.bundleName : obj.systemDisplayName) || '';
			return name.toLowerCase().includes(q);
		});
	}, [ deduped, search, ]);

	const isChecked = useCallback((obj) => {
		const uuids = new Set(pendingUUIDs);
		return getObjUUIDs(obj).some((u) => uuids.has(u));
	}, [ pendingUUIDs, getObjUUIDs, ]);

	const toggle = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			if (objUUIDs.some((u) => s.has(u))) { objUUIDs.forEach((u) => s.delete(u)); }
			else { objUUIDs.forEach((u) => s.add(u)); }
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const assignGroup = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			objUUIDs.forEach((u) => s.add(u));
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const clearGroup = useCallback((obj) => {
		const objUUIDs = getObjUUIDs(obj);
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			objUUIDs.forEach((u) => s.delete(u));
			return Array.from(s);
		});
	}, [ getObjUUIDs, ]);

	const assignProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			uuids.forEach((u) => s.add(u));
			return Array.from(s);
		});
	}, []);

	const clearProperty = useCallback((uuids) => {
		setPendingUUIDs((prev) => {
			const s = new Set(prev);
			uuids.forEach((u) => s.delete(u));
			return Array.from(s);
		});
	}, []);

	const assignAll = useCallback(() => {
		setPendingUUIDs((prev) => {
			const all = new Set(prev);
			(systemToSystemGroupObjects || []).forEach((obj) => Object.keys(obj.componentUUIDs).forEach((u) => all.add(u)));
			return Array.from(all);
		});
	}, [ systemToSystemGroupObjects, ]);

	const clearAll = useCallback(() => setPendingUUIDs([]), []);

	const save = useCallback(() => {
		onSave(pendingUUIDs);
		onClose();
	}, [ onSave, pendingUUIDs, onClose, ]);

	const propertyGroupItems = useMemo(() => {
		if (!fd) return {};
		const result = {};
		propertyGroups.forEach((pg) => {
			const pgSystemIDs = new Set();
			Object.entries(fd).forEach(([ key, val, ]) => {
				if (!key.startsWith(`system_group.${pg.sgUUID}`)) return;
				Object.keys(val).forEach((subKey) => {
					if (subKey.startsWith('system.')) pgSystemIDs.add(subKey.split('.')[1]);
				});
			});
			result[pg.sgUUID] = deduped.filter((obj) => {
				if (obj.isBundled) return (obj.systems || []).some((sysKey) => {
					const sysUUID = sysKey.startsWith('system.') ? sysKey.split('.')[1] : sysKey;
					return pgSystemIDs.has(sysUUID);
				});
				return pgSystemIDs.has(obj.systemID);
			});
		});
		return result;
	}, [ fd, propertyGroups, deduped, ]);

	const totalSystemsCount = deduped.length;

	const selectedSystemsCount = useMemo(() => {
		return deduped.filter((obj) => isChecked(obj)).length;
	}, [ deduped, isChecked, ]);

	const isAllAssigned = useMemo(() => {
		return deduped.length > 0 && deduped.every((obj) => isChecked(obj));
	}, [ deduped, isChecked, ]);

	const propertyRefsMap = useRef({});

	const isSearching = search.trim().length > 0;

	return (
		<div className={style.assignmentModalOverlay} onClick={onClose}>
			<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
				<div className={style.assignmentModalHeader}>
					<div className={style.assignmentModalTitleGroup}>
						<span className={style.assignmentModalTitle}>Assign To Systems</span>
					</div>
					<span className={style.assignmentModalClose} onClick={onClose}>×</span>
				</div>
				<div className={style.assignmentModalSearch}>
					<div className={style.assignmentModalSearchWrapper}>
						<span className={style.assignmentModalSearchIcon}><CustomIcon icon="search" /></span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`Search ${totalSystemsCount} systems...`}
							autoFocus
						/>
						{selectedSystemsCount > 0 && (
							<span className={style.assignmentModalSearchCount}>{selectedSystemsCount} selected</span>
						)}
					</div>
				</div>
				<div className={style.assignmentModalActions}>
					<label className={style.assignmentModalAssignAllLabel}>
						<input
							type="checkbox"
							checked={isAllAssigned}
							ref={(node) => { if (node) node.indeterminate = selectedSystemsCount > 0 && !isAllAssigned; }}
							onChange={(e) => { if (e.target.checked) { assignAll(); } else { clearAll(); } }}
							onClick={(e) => e.stopPropagation()}
						/>
						<span>Assign to all {totalSystemsCount} systems</span>
					</label>
					<div className="button" onClick={clearAll}>Clear All</div>
				</div>
				{!isSearching && propertyGroups.length > 0 && (
					<div className={style.assignmentModalQuickJump}>
						<span className={style.assignmentModalQuickJumpLabel}>Quick:</span>
						{propertyGroups.map((pg) => (
							<span
								key={pg.sgUUID}
								className={style.assignmentModalQuickJumpItem}
								onClick={() => {
									const el = propertyRefsMap.current[pg.sgUUID];
									if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
								}}
							>
								{pg.sgName}
							</span>
						))}
					</div>
				)}
				<div className={style.assignmentModalList}>
					{isSearching ? (
						<>
							{filtered.map((obj) => {
								const key = obj.isBundled ? obj.bundleID : obj.systemID;
								const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
								const allChecked = isChecked(obj);
								return (
									<div
										key={key}
										className={`${style.assignmentModalItem}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(obj)}
									>
										<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
										<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
										{obj.isBundled && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); allChecked ? clearGroup(obj) : assignGroup(obj); }}
											>
												{allChecked ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								);
							})}
							{filtered.length === 0 && <div className={style.assignmentModalEmpty}>No systems found.</div>}
						</>
					) : (
						propertyGroups.length > 0 ? propertyGroups.map((pg) => {
							const pgItems = propertyGroupItems[pg.sgUUID] || [];
							if (pgItems.length === 0) return null;
							const checkedCount = pgItems.filter((obj) => isChecked(obj)).length;
							const isPropertyAllChecked = pgItems.length > 0 && pgItems.every((obj) => isChecked(obj));
							const isPropertySomeChecked = pgItems.some((obj) => isChecked(obj));
							return (
								<React.Fragment key={pg.sgUUID}>
									<span ref={(el) => { propertyRefsMap.current[pg.sgUUID] = el; }} style={{ display: 'block', height: 0 }} />
									<div
										className={style.assignmentModalPropertyHeader}
									>
										<input
											type="checkbox"
											checked={isPropertyAllChecked}
											ref={(node) => { if (node) node.indeterminate = isPropertySomeChecked && !isPropertyAllChecked; }}
											onChange={(e) => { if (e.target.checked) { assignProperty(pg.uuids); } else { clearProperty(pg.uuids); } }}
											onClick={(e) => e.stopPropagation()}
										/>
										<span className={style.assignmentModalPropertyName}>{pg.sgName}</span>
										<span className={style.assignmentModalPropertyCount}>{checkedCount}/{pgItems.length}</span>
										<span
											className={`${style.assignmentModalToggleLink} ${style.assignmentModalPropertyToggle}`}
											onClick={(e) => { e.stopPropagation(); isPropertyAllChecked ? clearProperty(pg.uuids) : assignProperty(pg.uuids); }}
										>
											{isPropertyAllChecked ? 'Deselect all' : 'Select all'}
										</span>
									</div>
									{pgItems.map((obj) => {
										const key = obj.isBundled ? obj.bundleID : obj.systemID;
										const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
										return (
											<div
												key={key}
												className={`${style.assignmentModalItem} ${style.assignmentModalItemIndented}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
												onClick={() => toggle(obj)}
											>
												<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
												<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
												{obj.isBundled && (
													<span
														className={style.assignmentModalToggleLink}
														onClick={(e) => { e.stopPropagation(); isChecked(obj) ? clearGroup(obj) : assignGroup(obj); }}
													>
														{isChecked(obj) ? 'Deselect all' : 'Select all'}
													</span>
												)}
											</div>
										);
									})}
								</React.Fragment>
							);
						}) : (
							deduped.map((obj) => {
								const key = obj.isBundled ? obj.bundleID : obj.systemID;
								const label = obj.isBundled ? obj.bundleName : obj.systemDisplayName;
								const allChecked = isChecked(obj);
								return (
									<div
										key={key}
										className={`${style.assignmentModalItem}${obj.isBundled ? ` ${style.assignmentModalGroupItem}` : ''}`}
										onClick={() => toggle(obj)}
									>
										<input type="checkbox" checked={isChecked(obj)} onChange={() => toggle(obj)} onClick={(e) => e.stopPropagation()} />
										<span className={obj.isBundled ? style.assignmentModalGroupLabel : ''}>{label}</span>
										{obj.isBundled && (
											<span
												className={style.assignmentModalToggleLink}
												onClick={(e) => { e.stopPropagation(); allChecked ? clearGroup(obj) : assignGroup(obj); }}
											>
												{allChecked ? 'Deselect all' : 'Select all'}
											</span>
										)}
									</div>
								);
							})
						)
					)}
				</div>
				<div className={style.assignmentModalFooter}>
					<div className="button" onClick={save}>Save</div>
					<div className="button button-outlined" onClick={onClose}>Cancel</div>
				</div>
			</div>
		</div>
	);
};

const SlideshowTable = (props) => {
	const {
		app,
		fd,
		columnsToHide,
		toggleGroup,
		systemToSystemGroupObjects,
		array: initialArray,
		accountUUID,
		systemGroupUUID,
		componentUUID,
		addObject,
		editObject,
		buildPelicanData,
		deleteObjects,
		bulkDeleteSlides,
		addIframe,
		checkGroupForComponentRelation,
		openView,
		exportCSV,
	} = props;

	const [ itemsPerPage, setItemsPerPage, ] = useState(50);
	const [ page, setPage, ] = useState(1);
	const [ filteredRowsCount, setFilteredRowsCount, ] = useState(initialArray.length);
	const [ dataColumnWidthsPx, setDataColumnWidthsPx, ] = useState({});
	const [ hiddenDataColumnIds, setHiddenDataColumnIds, ] = useState([]);
	const [ selectedSlideKeys, setSelectedSlideKeys, ] = useState([]);
	const [ filteredSlideKeys, setFilteredSlideKeys, ] = useState([]);
	const [ hasReceivedFilteredRows, setHasReceivedFilteredRows, ] = useState(false);
	const [ isBulkDeleting, setIsBulkDeleting, ] = useState(false);
	const [ isBulkProcessing, setIsBulkProcessing, ] = useState(false);
	const [ showBulkAssignWarning, setShowBulkAssignWarning, ] = useState(false);
	const [ showBulkAssignModal, setShowBulkAssignModal, ] = useState(false);
	const [ assignmentModalSlide, setAssignmentModalSlide, ] = useState(null);
	const [ expandedPlayingOnSlideKeys, setExpandedPlayingOnSlideKeys, ] = useState(new Set());
	const [ propertyExpandedSlideKeys, setPropertyExpandedSlideKeys, ] = useState(new Set());
	const [ showColumnMenu, setShowColumnMenu, ] = useState(false);
	const [ columnMenuStyle, setColumnMenuStyle, ] = useState({
		position: 'fixed',
		top:      0,
		right:    0,
	});
	const columnMenuRef = useRef(null);
	const [ viewPrefsHydrated, setViewPrefsHydrated, ] = useState(false);
	const [ tableBodyHeight, setTableBodyHeight, ] = useState(null);
	const [ leftTableScrollerEl, setLeftTableScrollerEl, ] = useState(null);

	// Use sorted array directly since filtering is removed
	const data = React.useMemo(() => {
		return [ ...initialArray, ].sort((a, b) => {
			return a.order - b.order;
		});
	}, [ initialArray, ]);

	const [ globalSearchQuery, setGlobalSearchQuery, ] = useState('');

	useEffect(() => {
		setPage(1);
	}, [ globalSearchQuery, ]);

	const playingOnTextBySlideKey = useMemo(() => {
		if (!systemToSystemGroupObjects || !checkGroupForComponentRelation) return new Map();

		const systemToPropertyNames = new Map();
		if (fd) {
			Object.entries(fd).forEach(([ key, val, ]) => {
				if (!key.startsWith('system_group.')) return;
				const sgName = val && val.display_name ? val.display_name : '';
				if (!sgName) return;
				Object.keys(val).forEach((subKey) => {
					if (!subKey.startsWith('system.')) return;
					const sysID = subKey.split('.')[1];
					if (!systemToPropertyNames.has(sysID)) systemToPropertyNames.set(sysID, []);
					systemToPropertyNames.get(sysID).push(sgName.toLowerCase());
				});
			});
		}

		const map = new Map();
		data.forEach((slide) => {
			if (!slide || !slide.key) return;
			const parts = [];
			const seen = new Set();
			(systemToSystemGroupObjects || []).forEach((obj) => {
				const objKey = obj.isBundled ? obj.bundleID : obj.systemID;
				if (seen.has(objKey)) return;
				if (!checkGroupForComponentRelation(obj, slide)) return;
				seen.add(objKey);
				const name = obj.isBundled ? (obj.bundleName || '') : (obj.systemDisplayName || '');
				if (name) parts.push(name.toLowerCase());
				const sysIDs = obj.isBundled
					? (obj.systems || []).map((s) => s.split('.')[1])
					: [ obj.systemID, ];
				sysIDs.forEach((sysID) => {
					(systemToPropertyNames.get(sysID) || []).forEach((pName) => parts.push(pName));
				});
			});
			map.set(slide.key, parts.join(' '));
		});
		return map;
	}, [ data, systemToSystemGroupObjects, checkGroupForComponentRelation, fd, ]);

	const visibleData = useMemo(() => {
		if (!globalSearchQuery || globalSearchQuery.trim() === '') {
			return data;
		}

		const q = globalSearchQuery.toLowerCase();

		return data.filter((slide) => {
			const playingOnText = slide && slide.key ? (playingOnTextBySlideKey.get(slide.key) || '') : '';
			if (playingOnText.includes(q)) return true;

			try {
				return JSON.stringify(slide).toLowerCase().includes(q);
			} catch (err) {
				let hay = '';
				for (const k in slide) {
					try {
						const v = slide[k];
						if (v === null || typeof v === 'undefined') continue;
						if (typeof v === 'object') {
							hay += JSON.stringify(v) + ' ';
						} else {
							hay += String(v) + ' ';
						}
					} catch (e) {
					}
				}
				return hay.toLowerCase().includes(q);
			}
		});
	}, [ data, globalSearchQuery, playingOnTextBySlideKey, ]);

	const selectedSlideKeySet = useMemo(() => {
		return new Set(selectedSlideKeys);
	}, [ selectedSlideKeys, ]);

	const selectableSlideKeys = useMemo(() => {
		if (hasReceivedFilteredRows) {
			return filteredSlideKeys;
		}

		return data.map((slide) => {
			return slide?.key;
		}).filter(Boolean);
	}, [ data, filteredSlideKeys, hasReceivedFilteredRows, ]);

	const selectableSlideKeySet = useMemo(() => {
		return new Set(selectableSlideKeys);
	}, [ selectableSlideKeys, ]);

	useEffect(() => {
		const availableKeys = new Set(
			data
				.map((slide) => {
					return slide?.key;
				})
				.filter(Boolean)
		);

		setSelectedSlideKeys((prev) => {
			const next = prev.filter((slideKey) => {
				return availableKeys.has(slideKey);
			});
			return next.length === prev.length ? prev : next;
		});
	}, [ data, ]);

	const selectableSlides = useMemo(() => {
		return data.filter((slide) => {
			return selectableSlideKeySet.has(slide?.key);
		});
	}, [ data, selectableSlideKeySet, ]);

	const allSelectableSlidesSelected = useMemo(() => {
		if (selectableSlides.length === 0) {
			return false;
		}

		return selectableSlides.every((slide) => {
			return selectedSlideKeySet.has(slide.key);
		});
	}, [ selectableSlides, selectedSlideKeySet, ]);

	const someSelectableSlidesSelected = useMemo(() => {
		if (selectableSlides.length === 0) {
			return false;
		}

		return selectableSlides.some((slide) => {
			return selectedSlideKeySet.has(slide.key);
		});
	}, [ selectableSlides, selectedSlideKeySet, ]);

	const selectedSlides = useMemo(() => {
		return data.filter((slide) => {
			return selectedSlideKeySet.has(slide?.key);
		});
	}, [ data, selectedSlideKeySet, ]);

	const nonExclusiveComponentUUIDs = useMemo(() => {
		const ids = new Set();

		(systemToSystemGroupObjects || []).forEach((systemToSystemGroupObject) => {
			if (!systemToSystemGroupObject?.nonExclusive || !systemToSystemGroupObject.componentUUIDs) {
				return;
			}

			Object.keys(systemToSystemGroupObject.componentUUIDs).forEach((componentUUID) => {
				ids.add(componentUUID);
			});
		});

		return ids;
	}, [ systemToSystemGroupObjects, ]);

	const allComponentUUIDs = useMemo(() => {
		const ids = new Set();
		(systemToSystemGroupObjects || []).forEach((systemToSystemGroupObject) => {
			if (!systemToSystemGroupObject || !systemToSystemGroupObject.componentUUIDs) return;
			Object.keys(systemToSystemGroupObject.componentUUIDs).forEach((componentUUID) => {
				ids.add(componentUUID);
			});
		});
		return Array.from(ids);
	}, [ systemToSystemGroupObjects, ]);

	const hasNonExclusiveSelection = useMemo(() => {
		if (nonExclusiveComponentUUIDs.size === 0) {
			return false;
		}

		return selectedSlides.some((slide) => {
			const componentUUIDs = slide?.component_uuids || [];
			return componentUUIDs.some((componentUUID) => {
				return nonExclusiveComponentUUIDs.has(componentUUID);
			});
		});
	}, [ nonExclusiveComponentUUIDs, selectedSlides, ]);

	const toggleSlideSelection = useCallback((slideKey, isChecked) => {
		if (!slideKey) {
			return;
		}

		setSelectedSlideKeys((prev) => {
			const next = new Set(prev);

			if (isChecked) {
				next.add(slideKey);
			} else {
				next.delete(slideKey);
			}

			return Array.from(next);
		});
	}, []);

	const toggleSelectAllSlides = useCallback((isChecked) => {
		if (isChecked) {
			setSelectedSlideKeys((prev) => {
				const next = new Set(prev);

				selectableSlides.forEach((slide) => {
					if (slide.key) {
						next.add(slide.key);
					}
				});

				return Array.from(next);
			});
			return;
		}

		const selectableKeys = new Set(selectableSlides.map((slide) => {
			return slide.key;
		}));

		setSelectedSlideKeys((prev) => {
			return prev.filter((slideKey) => {
				return !selectableKeys.has(slideKey);
			});
		});
	}, [ selectableSlides, ]);

	const deleteSelectedSlides = useCallback((slidesToDelete) => {
		if (!slidesToDelete || slidesToDelete.length === 0) {
			return;
		}

		setIsBulkDeleting(true);

		// The backend decides per slide whether to unassign it from this group or
		// remove it; the frontend just sends the selection. Keep the Delete button
		// disabled for the duration of the request and clear in finally() (the
		// bulkDeleteSlides promise always settles, so this never strands the state).
		bulkDeleteSlides(slidesToDelete).finally(() => {
			setIsBulkDeleting(false);
			setSelectedSlideKeys([]);
		});
	}, [ bulkDeleteSlides, ]);

	const onDeleteSelectedClick = useCallback((e) => {
		e.preventDefault();

		if (isBulkDeleting || selectedSlides.length === 0) {
			return;
		}

		if (hasNonExclusiveSelection) {
			alert('Cannot delete slides that are assigned to systems outside this group.');
			return;
		}

		const slideLabel = selectedSlides.length === 1 ? 'this slide' : `${selectedSlides.length} slides`;
		if (!window.confirm(`Are you sure you want to delete ${slideLabel}?`)) {
			return;
		}

		deleteSelectedSlides(selectedSlides);
	}, [ deleteSelectedSlides, hasNonExclusiveSelection, isBulkDeleting, selectedSlides, ]);

	const pushSelectedToAll = useCallback((e) => {
		e && e.preventDefault();
		if (isBulkProcessing || selectedSlides.length === 0) return;
		if (!allComponentUUIDs || allComponentUUIDs.length === 0) {
			alert('No systems available to push to.');
			return;
		}
		const slideLabel = selectedSlides.length === 1 ? 'this slide' : `${selectedSlides.length} slides`;
		if (!window.confirm(`Are you sure you want to push ${slideLabel} to all systems?`)) {
			return;
		}
		setIsBulkProcessing(true);

		const runAtIndex = (index) => {
			if (index >= selectedSlides.length) {
				setIsBulkProcessing(false);
				buildPelicanData();
				return;
			}

			const slide = selectedSlides[index];
			if (!slide || !slide.key) {
				runAtIndex(index + 1);
				return;
			}

			const onComplete = () => runAtIndex(index + 1);

			try {
				const updated = {
					...slide,
					component_uuids: allComponentUUIDs,
				};
				editObject(updated, slide, onComplete, null, true);
			} catch (err) {
				onComplete();
			}
		};

		runAtIndex(0);
	}, [ isBulkProcessing, selectedSlides, allComponentUUIDs, editObject, buildPelicanData, ]);


	const saveBulkAssignments = useCallback((uuids) => {
		if (isBulkProcessing || selectedSlides.length === 0) return;
		setIsBulkProcessing(true);

		const runAtIndex = (index) => {
			if (index >= selectedSlides.length) {
				setIsBulkProcessing(false);
				buildPelicanData();
				return;
			}

			const slide = selectedSlides[index];
			if (!slide || !slide.key) {
				runAtIndex(index + 1);
				return;
			}

			const onComplete = () => runAtIndex(index + 1);

			try {
				const updated = {
					...slide,
					component_uuids: uuids,
				};
				editObject(updated, slide, onComplete, null, true);
			} catch (err) {
				onComplete();
			}
		};

		runAtIndex(0);
	}, [ isBulkProcessing, selectedSlides, editObject, buildPelicanData, ]);

	const clearAssignmentsForSelected = useCallback((e) => {
		e && e.preventDefault();
		if (isBulkProcessing || selectedSlides.length === 0) return;
		const slideLabel = selectedSlides.length === 1 ? 'this slide' : `${selectedSlides.length} slides`;
		if (!window.confirm(`Are you sure you want to clear all assignments for ${slideLabel}?`)) {
			return;
		}
		setIsBulkProcessing(true);

		const runAtIndex = (index) => {
			if (index >= selectedSlides.length) {
				setIsBulkProcessing(false);
				buildPelicanData();
				return;
			}

			const slide = selectedSlides[index];
			if (!slide || !slide.key) {
				runAtIndex(index + 1);
				return;
			}

			const onComplete = () => runAtIndex(index + 1);

			try {
				const updated = {
					...slide,
					component_uuids: [],
				};
				editObject(updated, slide, onComplete, null, true);
			} catch (err) {
				onComplete();
			}
		};

		runAtIndex(0);
	}, [ isBulkProcessing, selectedSlides, editObject, buildPelicanData, ]);

	const cancelSelection = useCallback((e) => {
		e && e.preventDefault();
		setSelectedSlideKeys([]);
	}, [ setSelectedSlideKeys, ]);


	// Add logging for initial render
	useEffect(() => {
		// SlideshowTable mounted
	}, [ initialArray, ]);

	useEffect(() => {
		setFilteredRowsCount(visibleData.length);
	}, [ visibleData, ]);

	useEffect(() => {
		setPage(1);
	}, [ visibleData, itemsPerPage, ]);

	useEffect(() => {
		if (app && app.setBulkProcessing) {
			app.setBulkProcessing(isBulkProcessing);
		}
	}, [ app, isBulkProcessing, ]);

	const onFilter = useCallback(() => {}, []);

	const onDataTableRowsChange = useCallback((rows = []) => {
		setHasReceivedFilteredRows(true);

		const safeRows = Array.isArray(rows) ? rows : [];
		const orderedFilteredRows = safeRows
			.map((row) => {
				return row?.original;
			})
			.filter(Boolean);

		setFilteredRowsCount(safeRows.length);

		const dedupedSortedKeys = Array.from(
			new Set(
				orderedFilteredRows
					.map((row) => {
						return row?.key;
					})
					.filter(Boolean)
			)
		).sort();

		setFilteredSlideKeys((prev) => {
			if (
				prev.length === dedupedSortedKeys.length &&
				prev.every((key, index) => {
					return key === dedupedSortedKeys[index];
				})
			) {
				return prev;
			}

			return dedupedSortedKeys;
		});
	}, []);

	const previousPageAvailable = page > 1;
	const nextPageAvailable = page * itemsPerPage < filteredRowsCount;

	const getResultsText = useCallback(() => {
		const start = Math.min((page - 1) * itemsPerPage + 1, filteredRowsCount);
		const end = Math.min(page * itemsPerPage, filteredRowsCount);
		if (filteredRowsCount <= 0) {
			return '0 results';
		}

		return `${start}-${end} of ${filteredRowsCount}`;
	}, [ filteredRowsCount, itemsPerPage, page, ]);

	// Layout and resize state for two-section table
	const wrapperRef = useRef(null);
	const resizingDataColsRef = useRef(false);
	const resizingDataColIdRef = useRef('');
	const startDataColXRef = useRef(0);
	const startDataColWidthRef = useRef(0);
	const startDataColAbsorbIdRef = useRef('');
	const startDataColAbsorbWidthRef = useRef(0);
	const visibleColumnIdsRef = useRef([]);
	const dragRafRef = useRef(null);
	const liveWidthsRef = useRef({});
	const dataColumnWidthsPxRef = useRef(dataColumnWidthsPx);
	const justFinishedResizingRef = useRef(false);

	const onLeftTableScrollerRef = useCallback((el) => {
		setLeftTableScrollerEl(el);
	}, []);

	useEffect(() => {
		const init = () => {
			// Calculate available height for the table body so sticky headers can work
			if (wrapperRef.current) {
				const wrapperRect = wrapperRef.current.getBoundingClientRect();
				const internalChromeHeight = 180;
				const available = window.innerHeight - Math.max(0, wrapperRect.top) - internalChromeHeight;
				setTableBodyHeight(Math.max(300, Math.floor(available)));
			}
		};
		init();
		window.addEventListener('resize', init);
		return () => window.removeEventListener('resize', init);
	}, []);

	useEffect(() => {
		dataColumnWidthsPxRef.current = dataColumnWidthsPx;
	}, [ dataColumnWidthsPx, ]);

	// Stable refs so slideshowColumns doesn't rebuild on every selection-state change.
	// Updated synchronously each render so Header/Cell always see the current value.
	const allSelectableSlidesSelectedRef = useRef(allSelectableSlidesSelected);
	allSelectableSlidesSelectedRef.current = allSelectableSlidesSelected;
	const someSelectableSlidesSelectedRef = useRef(someSelectableSlidesSelected);
	someSelectableSlidesSelectedRef.current = someSelectableSlidesSelected;
	const selectedSlideKeySetRef = useRef(selectedSlideKeySet);
	selectedSlideKeySetRef.current = selectedSlideKeySet;
	const toggleSlideSelectionRef = useRef(toggleSlideSelection);
	toggleSlideSelectionRef.current = toggleSlideSelection;
	const toggleSelectAllSlidesRef = useRef(toggleSelectAllSlides);
	toggleSelectAllSlidesRef.current = toggleSelectAllSlides;

	const togglePlayingOnExpand = useCallback((slideKey) => {
		setExpandedPlayingOnSlideKeys((prev) => {
			const next = new Set(prev);
			if (next.has(slideKey)) { next.delete(slideKey); } else { next.add(slideKey); }
			return next;
		});
	}, []);

	const playingOnPropsRef = useRef({});
	playingOnPropsRef.current = { systemToSystemGroupObjects, checkGroupForComponentRelation, fd, editObject, setAssignmentModalSlide, expandedPlayingOnSlideKeys, togglePlayingOnExpand, onPropertyExpandChange: (slideKey, hasExpanded) => {
		setPropertyExpandedSlideKeys((prev) => {
			const next = new Set(prev);
			if (hasExpanded) { next.add(slideKey); } else { next.delete(slideKey); }
			return next;
		});
	} };
	const createDisabledSquare = (systemName, slideshowName) => {
		return (
			<div className={style.disabledSquare} data-test-id={`system.${systemName} slideshow.${slideshowName}`}>
				<div className={style.checkboxIcon}>
					<CustomIcon icon="check"/>
				</div>
			</div>
		);
	};

	const createActiveCheckbox = (systemName, slideshowName) => {
		return (
			<div className={style.activeCheckbox} data-test-id={`system.${systemName} slideshow.${slideshowName}`}>
				<div className={style.checkboxIcon}>
					<CustomIcon icon="check"/>
				</div>
			</div>
		);
	};

	const slideshowColumns = useMemo(() => {
		const retVal = [
			{
				id:             'rowSelect',
				Header:         () => {
					const allSelected = allSelectableSlidesSelectedRef.current;
					const someSelected = someSelectableSlidesSelectedRef.current;
					const headerIsIndeterminate = someSelected && !allSelected;

					return (
						<input
							type="checkbox"
							className={style.rowSelectCheckbox}
							checked={allSelected}
							ref={(node) => {
								if (node) {
									node.indeterminate = headerIsIndeterminate;
								}
							}}
							onChange={(event) => {
								toggleSelectAllSlidesRef.current(event.target.checked);
							}}
							onClick={(event) => {
								event.stopPropagation();
							}}
							title={allSelected ? 'Deselect all slides' : 'Select all slides'}
						/>
					);
				},
				accessor:       'rowSelect',
				disableSortBy:  true,
				disableFilters: true,
				Cell:           ({
					row,
				}) => {
					const slideKey = row?.original?.key;
					const checked = slideKey ? selectedSlideKeySetRef.current.has(slideKey) : false;

					return (
						<input
							type="checkbox"
							className={style.rowSelectCheckbox}
							checked={checked}
							onChange={(event) => {
								toggleSlideSelectionRef.current(slideKey, event.target.checked);
							}}
							onClick={(event) => {
								event.stopPropagation();
							}}
							title={checked ? 'Deselect slide' : 'Select slide'}
						/>
					);
				},
			},
			{
				id:             'imgElm',
				Header:         'Slides',
				accessor:       'imgElm',
				disableFilters: true,
				disableSortBy:  true,
			},
			{
				id:       'description',
				Header:   'Description',
				accessor: 'description',
			},
			{
				id:             'order',
				Header:         'Order',
				accessor:       'order',
				disableFilters: true,
			},
			{
				id:       'duration',
				Header:   'Duration',
				accessor: 'duration',
				Cell:     ({
					value,
				}) => {
					return Math.round(value);
				},
			},
			{
				id:       'resolution',
				Header:   'Resolution',
				accessor: 'resolution',
			},
			{
				id:       'startDate',
				Header:   'Start Date',
				accessor: 'startDate',
				sortType: (a, b, _, isDescending) => {
					let modifier = isDescending ? 1 : -1;
					if (!a.original.start_date) {
						return modifier * -1;
					}
					if (!b.original.start_date) {
						return modifier * 1;
					}

					const aDate = new Date(a.original.start_date);
					const bDate = new Date(b.original.start_date);
					return aDate > bDate ? 1 : -1;
				},
			},
			{
				id:       'endDate',
				Header:   'End Date',
				accessor: 'endDate',
				sortType: (a, b, _, isDescending) => {
					let modifier = isDescending ? 1 : -1;
					if (!a.original.end_date) {
						return modifier * -1;
					}
					if (!b.original.end_date) {
						return modifier * 1;
					}

					const aDate = new Date(a.original.end_date);
					const bDate = new Date(b.original.end_date);
					return aDate > bDate ? 1 : -1;
				},
			},
			// Always-hidden columns (no longer in Show/Hide list)
			{
				id:       'startTime',
				Header:   'Start Time',
				accessor: 'startTime',
			},
			{
				id:       'endTime',
				Header:   'End Time',
				accessor: 'endTime',
			},
			{
				id:       'daysCol',
				Header:   'Days',
				accessor: 'daysCol',
				filter:   daysFilter,
			},
			{
				id:             'playingOn',
				Header:         'Playing On',
				accessor:       (row) => row,
				disableFilters: true,
				sortType:       (a, b) => {
					const aCount = (a.original.component_uuids || []).length;
					const bCount = (b.original.component_uuids || []).length;
					return aCount - bCount;
				},
				Cell: ({ value: slide }) => {
					const { systemToSystemGroupObjects: stsg, checkGroupForComponentRelation: cgcr, fd: fdCur, editObject: eo, setAssignmentModalSlide: setModal, expandedPlayingOnSlideKeys: expandedKeys, togglePlayingOnExpand: toggleExpand, onPropertyExpandChange } = playingOnPropsRef.current;
					const slideKey = slide && slide.key;
					return (
						<PlayingOnChips
							slide={slide}
							systemToSystemGroupObjects={stsg}
							checkGroupForComponentRelation={cgcr}
							fd={fdCur}
							editObject={eo}
							onOpenModal={(e) => { e && e.preventDefault(); e && e.stopPropagation(); setModal(slide); }}
							isExpanded={slideKey ? expandedKeys.has(slideKey) : false}
							onToggleExpand={() => slideKey && toggleExpand(slideKey)}
							onPropertyExpandChange={onPropertyExpandChange}
						/>
					);
				},
			},
		];


		let slideshowColumnsToPush = [];
		let systems = [];

		if (systemToSystemGroupObjects) {
			systemToSystemGroupObjects.forEach((systemToSystemGroupObject, i) => {
				if (systems.includes(systemToSystemGroupObject.systemID)) {
					return;
				}
				const fullHeaderName =
					systemToSystemGroupObject.isBundled ?
						systemToSystemGroupObject.bundleName :
						systemToSystemGroupObject.systemDisplayName;
				let displayName = systemToSystemGroupObject.systemDisplayName;

				if (systemToSystemGroupObject.isBundled) {
					if (systems.includes(systemToSystemGroupObject.bundleID)) {
						return;
					}
					let elms = [];
					systemToSystemGroupObject.systems.forEach((system, j) => {
						if (!fd[system]) {
							console.error(`System ${system} not found in fd`);
							return;
						}

						let systemDisplayName = fd[system].display_name;
						if (!elms.map((elm) => {
							return elm.props.children;
						}).includes(systemDisplayName)) {
							elms.push(<div key={`${systemDisplayName}_${j}`}>{systemDisplayName}</div>);
						}
					});

					elms = elms.sort((a, b) => {
						if (a.props.children > b.props.children) {
							return 1;
						}
						return -1;
					});

					let rolloverConfigData =
					<>
						<div className={style.rolloverHeader}>
							<CustomIcon icon="info-circle"/> {systemToSystemGroupObject.bundleName}
						</div>
						<div className={style.rolloverContent}>
							<div>This Content Group will display selected items on the following systems:</div>
							<br/>
							{elms}
						</div>
					</>;

					displayName = (
						<div key={`${systemToSystemGroupObject.systemID}_${i}`} className={style.configInfo}>
							<div className={style.configInfoHeader}>
								<div className={style.nameContainer}>
									{systemToSystemGroupObject.bundleName}
								</div>
								<div className={style.iconContainer}>
									<CustomIcon icon="info-circle"/>
								</div>
							</div>
							<div className={style.rolloverBox}>{rolloverConfigData}</div>
						</div>
					);
					systems.push(systemToSystemGroupObject.bundleID);
				} else {
					systems.push(systemToSystemGroupObject.systemID);
				}

				let idToUse = Object.keys(systemToSystemGroupObject.componentUUIDs).filter((uuid) => {
					if (uuid === systemToSystemGroupObject.bundleID) {
						return false;
					}

					return true;
				})[0];

				idToUse = systemToSystemGroupObject.systemID;

				const SelectAllCheckboxFilter = ({
					column: {
						id, preFilteredRows,
					},
				}) => {
					const checkboxRef = React.useRef();
					const [ checked, setChecked, ] = useState(false);
					const rowCount = preFilteredRows.length;
					let checkedRowCount = 0;
					for (let i = 0; i < preFilteredRows.length; i++) {
						if (checkGroupForComponentRelation(systemToSystemGroupObject, preFilteredRows[i].original)) {
							checkedRowCount++;
						}
					}

					const allRowsChecked = checkedRowCount === rowCount;

					useEffect(() => {
						setChecked(allRowsChecked);

						if (checkboxRef.current) {
							checkboxRef.current.indeterminate = checkedRowCount > 0 && checkedRowCount < rowCount;
						}
					}, [ allRowsChecked, checkedRowCount, rowCount, ]);

					const onChange = (e) => {
						const isChecked = e.target.checked;
						const toggleRows = [];
						const groupID = id.split('.')[1];

						for (let i = 0; i < preFilteredRows.length; i++) {
							const hasGreenCheck = checkGroupForComponentRelation(systemToSystemGroupObject, preFilteredRows[i].original);

							if ((isChecked && !hasGreenCheck) || (!isChecked && hasGreenCheck)) {
								toggleRows.push(preFilteredRows[i].original);
							}
						}

						if (toggleRows.length > 0) {
							toggleGroup(groupID, ...toggleRows);
						}

						setChecked(isChecked);
					};

					if (rowCount === 0) {
						return null;
					}

					return (
						<input
							type="checkbox"
							ref={checkboxRef}
							checked={checked}
							onChange={onChange}
							onClick={(e) => {
								e.stopPropagation();
							}}
							title={`Select ${allRowsChecked ? 'None' : 'All'}`}
						/>
					);
				};


			slideshowColumnsToPush.push({
				id:       `groups.${idToUse}`,
				Header:   displayName,
				fullHeaderName,
				systemMeta: systemToSystemGroupObject,
				Filter:   SelectAllCheckboxFilter,
				accessor: (d) => {
					return checkGroupForComponentRelation(systemToSystemGroupObject, d) ? createActiveCheckbox(systemToSystemGroupObject.systemDisplayName, d.description) : createDisabledSquare(systemToSystemGroupObject.systemDisplayName, d.description);
				},
				sortType: (a, b) => {
					return checkGroupForComponentRelation(systemToSystemGroupObject, b.original) - checkGroupForComponentRelation(systemToSystemGroupObject, a.original);
				},
				// disableFilters: true,
			});
			});
		}

		slideshowColumnsToPush = slideshowColumnsToPush.sort((a, b) => {
			if (typeof a.Header !== 'string' && typeof b.Header !== 'string') {
				return 1;
			} else if (typeof a.Header !== 'string') {
				return -1;
			} else if (typeof b.Header !== 'string') {
				return 1;
			}
			return a.Header > b.Header ? 1 : -1;
		});

		// keep data columns separate from system/group columns
		return {
			dataColumns: retVal,
			systemColumns: slideshowColumnsToPush,
		};
	}, [ checkGroupForComponentRelation, fd, systemToSystemGroupObjects, toggleGroup, ]);

	const dataColumnIds = useMemo(() => {
		return slideshowColumns.dataColumns.map((c) => c.id).filter((id) => Boolean(id) && id !== 'rowSelect');
	}, [ slideshowColumns, ]);

	const hiddenSystemSortColumnIds = useMemo(() => {
		return slideshowColumns.systemColumns
			.map((column) => column.id)
			.filter((columnId) => typeof columnId === 'string');
	}, [ slideshowColumns, ]);

	const visibleDataColumns = useMemo(() => {
		return slideshowColumns.dataColumns.filter((column) => {
			return !hiddenDataColumnIds.includes(column.id);
		});
	}, [ hiddenDataColumnIds, slideshowColumns, ]);

	visibleColumnIdsRef.current = visibleDataColumns.map((c) => c.id).filter((id) => id && id !== 'rowSelect');

	const dataColumns = React.useMemo(() => {
		return [
			{
				Header:        'SlideshowHeader',
				columns:       [ ...visibleDataColumns, ...slideshowColumns.systemColumns, ],
				initialSortBy: [
					{
						id:   'description',
						desc: false,
					},
				],
			},
		];
	}, [ slideshowColumns.systemColumns, visibleDataColumns ]);

	const viewCacheKey = useMemo(() => {
		return getSlideshowViewCacheKey(accountUUID);
	}, [ accountUUID, ]);

	useEffect(() => {
		setViewPrefsHydrated(false);
	}, [ viewCacheKey, ]);

	useEffect(() => {
		let cachedPrefs = null;
		try {
			const cachedJSON = localStorage.getItem(viewCacheKey);
			if (cachedJSON) {
				cachedPrefs = JSON.parse(cachedJSON);
			}
		} catch (err) {
			cachedPrefs = null;
		}

		const safeCachedPrefs = {
			...defaultSlideshowViewPrefs,
			...(cachedPrefs || {}),
		};

		const cachedHidden = Array.isArray(safeCachedPrefs.hiddenDataColumnIds) ? safeCachedPrefs.hiddenDataColumnIds : [];
		setHiddenDataColumnIds(cachedHidden.filter((id) => typeof id === 'string' && id !== 'imgElm' && id !== 'description'));

		if (safeCachedPrefs.dataColumnWidthsPx && typeof safeCachedPrefs.dataColumnWidthsPx === 'object') {
			setDataColumnWidthsPx(safeCachedPrefs.dataColumnWidthsPx);
		}

		setViewPrefsHydrated(true);
	}, [ viewCacheKey, ]);

	useEffect(() => {
		if (!viewPrefsHydrated) {
			return;
		}

		const payload = {
			hiddenDataColumnIds,
			dataColumnWidthsPx,
		};

		try {
			localStorage.setItem(viewCacheKey, JSON.stringify(payload));
		} catch (err) {
		}
	}, [ dataColumnWidthsPx, hiddenDataColumnIds, viewCacheKey, viewPrefsHydrated, ]);

	useEffect(() => {
		if (dataColumnIds.length === 0) {
			return;
		}

		setDataColumnWidthsPx((prev) => {
			if (Object.keys(prev).length > 0) {
				return prev;
			}

			const containerW = wrapperRef.current ? wrapperRef.current.clientWidth : window.innerWidth;
			const defaultHidden = new Set(defaultSlideshowViewPrefs.hiddenDataColumnIds);
			const defaultVisibleIds = [ 'rowSelect', ...dataColumnIds.filter((id) => !defaultHidden.has(id)), ];
			return computeDefaultDataColumnWidths(defaultVisibleIds, containerW);
		});
	}, [ dataColumnIds, ]);

	const onDataColHandleMouseMove = useCallback((e) => {
		if (!resizingDataColsRef.current || !resizingDataColIdRef.current) {
			return;
		}
		if (dragRafRef.current !== null) return;
		const clientX = e.clientX;
		dragRafRef.current = requestAnimationFrame(() => {
			dragRafRef.current = null;
			if (!wrapperRef.current) return;
			const containerWidth = wrapperRef.current.clientWidth;
			if (containerWidth <= 0) return;

			const columnId = resizingDataColIdRef.current;
			const dx = clientX - startDataColXRef.current;

			const minWidth = columnId === 'playingOn'
				? Math.round(containerWidth * 0.20)
				: (DATA_COLUMN_MIN_WIDTHS[columnId] || DEFAULT_DATA_COL_MIN_WIDTH);

			const absorbId = startDataColAbsorbIdRef.current;
			const absorbMin = absorbId
				? (absorbId === 'playingOn'
					? Math.round(containerWidth * 0.20)
					: (DATA_COLUMN_MIN_WIDTHS[absorbId] || DEFAULT_DATA_COL_MIN_WIDTH))
				: 0;

			const maxGrowth = absorbId
				? Math.max(0, startDataColAbsorbWidthRef.current - absorbMin)
				: Infinity;
			const newWidth = Math.max(minWidth, Math.min(startDataColWidthRef.current + maxGrowth, startDataColWidthRef.current + dx));

			let absorbNew = 0;
			if (absorbId) {
				const delta = newWidth - startDataColWidthRef.current;
				absorbNew = startDataColAbsorbWidthRef.current - delta; // bounded by maxGrowth, no clamp needed
			}

			liveWidthsRef.current = { ...liveWidthsRef.current, [columnId]: newWidth };
			if (absorbId) liveWidthsRef.current[absorbId] = absorbNew;

			const idsToUpdate = absorbId ? [ columnId, absorbId ] : [ columnId ];
			idsToUpdate.forEach((id) => {
				const w = liveWidthsRef.current[id];
				wrapperRef.current.querySelectorAll(`[data-col-id="${id}"]`).forEach((el) => {
					el.style.width = `${w}px`;
					el.style.minWidth = `${w}px`;
					el.style.maxWidth = `${w}px`;
				});
			});
		});
	}, []);

	const onDataColHandleMouseUp = useCallback(() => {
		resizingDataColsRef.current = false;
		resizingDataColIdRef.current = '';
		justFinishedResizingRef.current = true;
		setTimeout(() => {
			justFinishedResizingRef.current = false;
		}, 50);
		if (dragRafRef.current !== null) {
			cancelAnimationFrame(dragRafRef.current);
			dragRafRef.current = null;
		}
		const finalWidths = liveWidthsRef.current;
		if (Object.keys(finalWidths).length > 0) {
			setDataColumnWidthsPx((prev) => ({ ...prev, ...finalWidths }));
			liveWidthsRef.current = {};
		}
		document.removeEventListener('mousemove', onDataColHandleMouseMove);
		document.removeEventListener('mouseup', onDataColHandleMouseUp);
	}, [ onDataColHandleMouseMove, ]);

	const onDataColHandleMouseDown = useCallback((e, columnId) => {
		e.preventDefault();
		e.stopPropagation();
		resizingDataColsRef.current = true;
		resizingDataColIdRef.current = columnId;
		startDataColXRef.current = e.clientX;

		const headerCell = e.currentTarget ? e.currentTarget.parentElement : null;
		const measuredWidth = headerCell ? Math.round(headerCell.getBoundingClientRect().width) : 0;
		startDataColWidthRef.current = measuredWidth || dataColumnWidthsPx[columnId] || 120;

		const visibleIds = visibleColumnIdsRef.current;
		const dragIdx = visibleIds.indexOf(columnId);
		const nextId = dragIdx >= 0 && dragIdx < visibleIds.length - 1 ? visibleIds[dragIdx + 1] : '';
		startDataColAbsorbIdRef.current = nextId;

		if (nextId) {
			const nextHeader = headerCell ? headerCell.nextElementSibling : null;
			const measuredAbsorbWidth = nextHeader ? Math.round(nextHeader.getBoundingClientRect().width) : 0;
			startDataColAbsorbWidthRef.current = measuredAbsorbWidth || dataColumnWidthsPx[nextId] || DEFAULT_DATA_COL_WIDTHS[nextId] || DATA_COLUMN_MIN_WIDTHS[nextId] || DEFAULT_DATA_COL_MIN_WIDTH;
		} else {
			startDataColAbsorbWidthRef.current = 0;
		}

		document.addEventListener('mousemove', onDataColHandleMouseMove);
		document.addEventListener('mouseup', onDataColHandleMouseUp);
	}, [ dataColumnWidthsPx, onDataColHandleMouseMove, onDataColHandleMouseUp, ]);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', onDataColHandleMouseMove);
			document.removeEventListener('mouseup', onDataColHandleMouseUp);
		};
	}, [ onDataColHandleMouseMove, onDataColHandleMouseUp, ]);

	const getDataColumnStyle = useCallback((columnId) => {
		if (!columnId) {
			return {};
		}

		const nextStyle = {};

		if (columnId === 'rowSelect') {
			nextStyle.width = `${ROW_SELECT_COLUMN_WIDTH_PX}px`;
			nextStyle.minWidth = `${ROW_SELECT_COLUMN_WIDTH_PX}px`;
			nextStyle.maxWidth = `${ROW_SELECT_COLUMN_WIDTH_PX}px`;
			nextStyle.left = '0px';
			return nextStyle;
		}

		const containerWidth = wrapperRef.current && wrapperRef.current.clientWidth > 0
			? wrapperRef.current.clientWidth
			: window.innerWidth;
		if (containerWidth <= 0) return {};
		const availableWidth = containerWidth - ROW_SELECT_COLUMN_WIDTH_PX;
		const visibleIds = visibleColumnIdsRef.current; // excludes rowSelect
		const totalStoredWidth = visibleIds.reduce((sum, id) => {
			return sum + (dataColumnWidthsPx[id] || DATA_COLUMN_MIN_WIDTHS[id] || DEFAULT_DATA_COL_MIN_WIDTH);
		}, 0);
		const storedWidth = dataColumnWidthsPx[columnId] || DATA_COLUMN_MIN_WIDTHS[columnId] || DEFAULT_DATA_COL_MIN_WIDTH;
		const scaledWidth = totalStoredWidth > 0
			? Math.round(storedWidth * availableWidth / totalStoredWidth)
			: storedWidth;

		const effectiveWidth = columnId === 'playingOn'
			? Math.max(scaledWidth, Math.round(containerWidth * 0.20))
			: scaledWidth;

		nextStyle.width = `${effectiveWidth}px`;
		nextStyle.minWidth = `${effectiveWidth}px`;
		nextStyle.maxWidth = `${effectiveWidth}px`;

		if (columnId === 'description') {
			nextStyle.overflow = 'hidden';
		}
		if (columnId === 'order' || columnId === 'duration' || columnId === 'resolution' || columnId === 'startDate' || columnId === 'endDate') {
			nextStyle.textAlign = 'center';
		}

		return nextStyle;
	}, [ dataColumnWidthsPx, ]);

	const theadColStylesForData = useCallback((column) => {
		return getDataColumnStyle(column?.id || '');
	}, [ getDataColumnStyle, ]);

	const filterRowColStylesForData = useCallback((column) => {
		return getDataColumnStyle(column?.id || '');
	}, [ getDataColumnStyle, ]);

	const tbodyColStylesForData = useCallback((cell) => {
		return getDataColumnStyle(cell?.column?.id || '');
	}, [ getDataColumnStyle, ]);

	const isDataColumnResizable = useCallback((column) => {
		return dataColumnIds.includes(column?.id || '');
	}, [ dataColumnIds, ]);

	const dataColumnOptions = useMemo(() => {
		return slideshowColumns.dataColumns
			.filter((column) => {
				return column.id !== 'rowSelect';
			})
			.map((column) => {
			return {
				id:    column.id,
				label: typeof column.Header === 'string' ? column.Header : column.id,
			};
		});
	}, [ slideshowColumns, ]);

	const showHideColumnList = useMemo(() => {
		return [
			{ id: 'imgElm',      label: 'Image',       alwaysOn: true },
			{ id: 'description', label: 'Description',  alwaysOn: true },
			{ id: 'order',       label: 'Order' },
			{ id: 'duration',    label: 'Duration' },
			{ id: 'resolution',  label: 'Resolution' },
			{ id: 'startDate',   label: 'Start Date' },
			{ id: 'endDate',     label: 'End Date' },
		];
	}, []);

	const onShowHideButtonClick = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();

		if (showColumnMenu) {
			setShowColumnMenu(false);
			return;
		}

		const rect = e.currentTarget.getBoundingClientRect();
		setColumnMenuStyle({
			position: 'fixed',
			top:      `${Math.round(rect.bottom + 6)}px`,
			right:    `${Math.round(window.innerWidth - rect.right)}px`,
		});
		setShowColumnMenu(true);
	}, [ showColumnMenu, ]);

	useEffect(() => {
		if (!showColumnMenu) {
			return;
		}

		const onDocClick = (e) => {
			if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
				setShowColumnMenu(false);
			}
		};

		document.addEventListener('mousedown', onDocClick);
		return () => {
			document.removeEventListener('mousedown', onDocClick);
		};
	}, [ showColumnMenu, ]);

	const toggleDataColumnVisibility = useCallback((columnId) => {
		setHiddenDataColumnIds((prev) => {
			if (prev.includes(columnId)) {
				return prev.filter((id) => {
					return id !== columnId;
				});
			}

			const visibleCount = dataColumnIds.length - prev.length;
			if (visibleCount <= 1) {
				return prev;
			}

			return [ ...prev, columnId, ];
		});
	}, [ dataColumnIds, ]);

	const secondaryNavButtons = useMemo(() => {
		return [
			{
				className: 'button',
				onClick:   (e) => {
					e.preventDefault();

					let hasPendingSlideshowChanges = app.hasPendingSlideshowChanges();

					const noSaveCB = () => {
						buildPelicanData();
					};

					const saveCB = () => {
						buildPelicanData();
						openAddModal();
					};

					const openAddModal = () => {
						app.openModal({
							modalSize:  2,
							showModal:  true,
							modalProps: {
								title:          'Add Slide',
								customClass:    'test',
								pendingChanges: false,
								jsx:            (
									<SlideshowModal
										app={app}
										isEdit={false}
										accountUUID={accountUUID}
										systemGroupUUID={systemGroupUUID}
										addObject={addObject}
										componentUUID={componentUUID}
										addImageReference={props.addImageReference}
										addIframe={addIframe}
										systemToSystemGroupObjects={systemToSystemGroupObjects}
										buildPelicanData={buildPelicanData}
										allowSlideshowUrl={getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`)}
										fd={fd}
									/>
								),
							},
						});
					};

					if (hasPendingSlideshowChanges) {
						app.openPendingChangesModal('content', noSaveCB, saveCB);
					} else {
						openAddModal();
					}
				},
				icon:       'plus',
				text:       'Add New',
				dataTestId: 'addNewBtn',
			},
			{
				onClick: (e) => {
					e.preventDefault();
					exportCSV();
				},
				className:  'button',
				icon:       'file-download',
				text:       'Export',
				dataTestId: 'exportBtn',
			},
			{
				onClick: onDeleteSelectedClick,
				className: `button${(selectedSlides.length > 0 && !isBulkDeleting) ? '' : ' button-disabled'}`,
				icon:      'trash',
				text:      isBulkDeleting ? 'Deleting...' : `Delete${selectedSlides.length > 0 ? ` (${selectedSlides.length})` : ''}`,
			},
		];
	}, [ accountUUID, addIframe, addObject, app, buildPelicanData, componentUUID, exportCSV, fd, isBulkDeleting, onDeleteSelectedClick, props.addImageReference, selectedSlides.length, systemGroupUUID, systemToSystemGroupObjects, ]);

	const tertiaryNavButtons = useMemo(() => {
		const totalPages = Math.ceil(filteredRowsCount / itemsPerPage) || 1;
		const currentPage = Math.min(page, totalPages);
		const maxVisiblePages = 5;
		let startPage = Math.max(1, currentPage - 2);
		let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

		if (endPage - startPage < maxVisiblePages - 1) {
			startPage = Math.max(1, endPage - maxVisiblePages + 1);
		}

		const pageNumbers = [];
		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i);
		}

		return [
			{
				className: `button` + (previousPageAvailable ? '' : ` button-disabled`),
				onClick:   () => {
					return previousPageAvailable && setPage((p) => {
						return Math.max(1, p - 1);
					});
				},
				text: '< Previous',
			},
			...pageNumbers.map((num) => {
				return {
					className: `button ${currentPage === num ? 'active' : ''}`,
					onClick:   () => {
						return setPage(num);
					},
					text: num.toString(),
				};
			}),
			{
				className: `button` + (nextPageAvailable ? '' : ` button-disabled`),
				onClick:   () => {
					return nextPageAvailable && setPage((p) => {
						return p + 1;
					});
				},
				text: 'Next >',
			},
		];
	}, [ previousPageAvailable, nextPageAvailable, page, filteredRowsCount, itemsPerPage, ]);

	const cellOnClick = (cell, row) => {
		return (e) => {
			e.preventDefault();
			if (cell.column.id === 'rowSelect' || cell.column.id === 'playingOn') {
				return;
			}

			if (cell.column.id.startsWith('groups.', 0)) {
				let groupID = cell.column.id.split('.')[1];
				toggleGroup(groupID, row.original);
			} else {
				// Mirror "Add New": clear any foreign pending changes before opening
				// the edit modal, so an open Slideshow modal never carries changes
				// from another scope — the precondition the atomic save relies on.
				const openEditModal = () => {
					app.openModal({
						modalSize:  2,
						showModal:  true,
						modalProps: {
							title:          'Edit Slide',
							customClass:    'test',
							pendingChanges: false,
							jsx:            (
								<SlideshowModal
									addImageReference={props.addImageReference}
									isEdit={true}
									accountUUID={accountUUID}
									systemGroupUUID={systemGroupUUID}
									file={cell.row.original}
									editObject={editObject}
									deleteObjects={deleteObjects}
									systemToSystemGroupObjects={systemToSystemGroupObjects}
									buildPelicanData={buildPelicanData}
									allowSlideshowUrl={getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`)}
									fd={fd}
									app={app}
								/>
							),
						},
					});
				};

				if (app.hasPendingSlideshowChanges()) {
					app.openPendingChangesModal('content', () => {
						buildPelicanData();
					}, () => {
						buildPelicanData();
						openEditModal();
					});
				} else {
					openEditModal();
				}
			}
		};
	};

	const tableScrollStyle = tableBodyHeight ? { height: `${tableBodyHeight}px`, overflowY: 'auto' } : {};

	return (
		<div ref={wrapperRef} style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
			{showColumnMenu && (
				<div
					ref={columnMenuRef}
					className={style.slideshowColumnMenu}
					style={columnMenuStyle}
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<div className={style.slideshowColumnMenuTitle}>Columns</div>
					{showHideColumnList.map((columnOption) => {
						const isVisible = !hiddenDataColumnIds.includes(columnOption.id);
						const visibleDataCount = dataColumnIds.length - hiddenDataColumnIds.length;
						const disableHide = columnOption.alwaysOn || (isVisible && visibleDataCount <= 1);
						return (
							<label key={columnOption.id} className={style.slideshowColumnMenuItem}>
								<input
									type="checkbox"
									checked={isVisible}
									disabled={disableHide}
									onChange={() => {
										if (!columnOption.alwaysOn) toggleDataColumnVisibility(columnOption.id);
									}}
									onClick={(e) => {
										e.stopPropagation();
									}}
								/>
								<span>{columnOption.label}</span>
							</label>
						);
					})}
					{/* Intentionally only the seven specified columns are listed per feature requirements. */}
				</div>
			)}

			<div className='secondaryNav topNav'>
				{secondaryNavButtons.map((button, i) => {
					return (
						<div
							key={`button_${i}`}
							className={button.className}
							onClick={button.onClick}
							data-test-id={button.dataTestId}
						>
							{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
						</div>
					);
				})}
			</div>

			{/* Search bar + bulk actions + show/hide — all on one row */}
			<div className={`secondaryNav ${style.slideshowToolbar}`}>
				<div className={style.slideshowSearchContainer}>
					<div className={style.slideshowTopSearch} onClick={(e) => e.stopPropagation()}>
						<CustomIcon icon="search"/>
						<input
							type="search"
							value={globalSearchQuery}
							onChange={(e) => setGlobalSearchQuery(e.target.value)}
							placeholder={`Search ${data.length} slides`}
							aria-label="Search slides"
							onClick={(e) => e.stopPropagation()}
						/>
						{globalSearchQuery ? (
							<div
								className={'button'}
								onClick={(e) => {
									e.preventDefault();
									setGlobalSearchQuery('');
								}}
							>
								Clear
							</div>
						) : null}
					</div>
				</div>

				<div className={style.slideshowToolbarRight}>
					{selectedSlides.length > 0 && (
						<div className={style.bulkActionBar}>
							<div className={style.bulkActionCount}>{`${selectedSlides.length} item${selectedSlides.length > 1 ? 's' : ''} selected`}</div>
							<div className={style.bulkActionDivider} />
							<div style={{ display: 'flex', gap: '8px' }}>
								<div className={'button'} onClick={() => setShowBulkAssignWarning(true)}>
									Assign Selected
								</div>
								<div className={style.bulkActionDivider} />
								<div className={'button'} onClick={pushSelectedToAll}>
									Push to All
								</div>
								<div className={'button'} onClick={clearAssignmentsForSelected}>
									Clear All Assignments
								</div>
								<div className={'button button-outlined'} onClick={cancelSelection}>
									Cancel
								</div>
							</div>
						</div>
					)}

					<div
						className={'button'}
						onClick={onShowHideButtonClick}
						data-test-id="slideshowColumnsBtn"
					>
						<CustomIcon icon={'columns'} /> Show/Hide
					</div>
				</div>
			</div>

			<div style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
				<VultureTable
					style={style}
					columns={dataColumns}
					data={visibleData}
					fd={fd}
					columnsToHide={hiddenSystemSortColumnIds}
					secondaryNavButtons={[]}
					tertiaryNavButtons={tertiaryNavButtons}
					showSecondaryNav={false}
					hideFilterRow={true}
					cellOnClick={cellOnClick}
					page={page}
					itemsPerPage={itemsPerPage}
					totalResults={filteredRowsCount}
					onItemsPerPageChange={(newValue) => {
						setItemsPerPage(newValue);
						setPage(1);
					}}
					onFilter={onFilter}
					onFilteredRowsChange={onDataTableRowsChange}
					theadColStyles={theadColStylesForData}
					filterRowColStyles={filterRowColStylesForData}
					tbodyColStyles={tbodyColStylesForData}
					onColumnResizeMouseDown={onDataColHandleMouseDown}
					isColumnResizable={isDataColumnResizable}
					preventSortRef={justFinishedResizingRef}
					initialState={{
						sortBy: [
							{
								id:   'description',
								desc: false,
							},
						],
					}}
					rowProps={(row) => {
						const slideKey = row?.original?.key;
						const isPlayingOnExpanded = slideKey
							? (expandedPlayingOnSlideKeys.has(slideKey) || propertyExpandedSlideKeys.has(slideKey))
							: false;
						return {
							index: row.index,
							row,
							'data-playingon-expanded': isPlayingOnExpanded ? 'true' : undefined,
						};
					}}
					showResultsAndRowsDropdown={false}
					tableScrollStyle={tableScrollStyle}
					tableScrollerRefCallback={onLeftTableScrollerRef}
				/>
			</div>
			
			{showBulkAssignWarning && (
				<div className={style.assignmentModalOverlay} onClick={() => setShowBulkAssignWarning(false)} style={{ zIndex: 9999 }}>
					<div className={style.assignmentModal} onClick={(e) => e.stopPropagation()}>
						<div className={style.assignmentModalHeader}>
							<div className={style.assignmentModalTitleGroup}>
								<span className={style.assignmentModalTitle}>Warning</span>
							</div>
							<span className={style.assignmentModalClose} onClick={() => setShowBulkAssignWarning(false)}>×</span>
						</div>
						<div className={style.assignmentModalSearch} style={{ padding: '16px' }}>
							Choosing this method will override all existing system assignments.
						</div>
						<div className={style.assignmentModalFooter}>
							<div className="button" onClick={() => { setShowBulkAssignWarning(false); setShowBulkAssignModal(true); }}>Proceed</div>
							<div className="button button-outlined" onClick={() => setShowBulkAssignWarning(false)}>Cancel</div>
						</div>
					</div>
				</div>
			)}
			{showBulkAssignModal && (
				<BulkSlideAssignmentModal
					systemToSystemGroupObjects={systemToSystemGroupObjects}
					fd={fd}
					onSave={(uuids) => {
						saveBulkAssignments(uuids);
						setShowBulkAssignModal(false);
					}}
					onClose={() => setShowBulkAssignModal(false)}
				/>
			)}

			{assignmentModalSlide && (
				<SystemAssignmentModal
					slide={assignmentModalSlide}
					systemToSystemGroupObjects={systemToSystemGroupObjects}
					checkGroupForComponentRelation={checkGroupForComponentRelation}
					fd={fd}
					editObject={editObject}
					buildPelicanData={buildPelicanData}
					onClose={() => setAssignmentModalSlide(null)}
				/>
			)}

			<div className={`secondaryNav ${style.sharedFooterControls}`}>
				<div className={style.resultsText}>
					{getResultsText()}
				</div>
				<div className={style.paginationContainer}>
					{tertiaryNavButtons.map((button, i) => {
						return (
							<div
								key={`footer_button_${i}`}
								className={button.className}
								onClick={button.onClick}
							>
								{button.icon ? <CustomIcon icon={button.icon}/> : ''}{button.text}
							</div>
						);
					})}
				</div>
				<div className={style.rowsPerPageDropdown}>
					<select
						value={itemsPerPage}
						onChange={(e) => {
							const newValue = parseInt(e.target.value, 10);
							setItemsPerPage(newValue);
							setPage(1);
						}}
					>
						{[ 5, 10, 25, 50, 100, ].map((option) => {
							return (
								<option key={option} value={option}>
									{option} per page
								</option>
							);
						})}
					</select>
				</div>
			</div>
		</div>
	);
};

export default SlideshowTable;